// pages/Notifications/NotificationsPage.jsx
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Bell, CheckCheck, Trash2, Clock,
  ArrowLeft, Package, Star, Heart, User, LogIn, ShoppingBag 
} from 'lucide-react';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { authFetch } from '../../helpers/authFetch'

function NotificationsPage() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, unread, read
  const [typeFilter, setTypeFilter] = useState('all'); // all, product_added, product_reviewed, etc.
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [total, setTotal] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);

  const notificationTypes = [
    { value: 'all', label: 'All Notifications', icon: '🔔' },
    { value: 'product_added', label: 'New Products', icon: '📦' },
    { value: 'product_reviewed', label: 'Product Reviews', icon: '⭐' },
    { value: 'product_liked', label: 'Product Likes', icon: '❤️' },
    { value: 'profile_updated', label: 'Profile Updates', icon: '👤' },
    { value: 'login', label: 'Login Events', icon: '🔐' },
    { value: 'order_status', label: 'Order Status', icon: '📋' }
  ];

  // Fetch notifications
  const fetchNotifications = async (pageNum = 0) => {
    try {
      setLoading(true);
      
      let unreadOnly = false;
      if (filter === 'unread') unreadOnly = true;

      const response = await authFetch(
        `${summaryApi.getNotifications?.url || 'https://josiesphones-server.onrender.com/api/notifications/get-notifications'}?limit=15&skip=${pageNum * 15}&unreadOnly=${unreadOnly}`,
        {
          method: 'GET',
          
        }
      );
      
      const data = await response.json();
      
      if (data.success) {
        let filteredData = data.data;

        // Apply type filter
        if (typeFilter !== 'all') {
          filteredData = filteredData.filter(notif => notif.type === typeFilter);
        }

        // Apply read filter
        if (filter === 'read') {
          filteredData = filteredData.filter(notif => notif.isRead);
        }

        setNotifications(filteredData);
        setUnreadCount(data.unreadCount);
        setTotal(data.total);
        setHasMore(data.hasMore);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  // Mark as read
  const markAsRead = async (notificationId) => {
    try {
      await authFetch(
        `${summaryApi.markAsRead?.url || 'http://localhost:8080/api/notifications/mark-as-read'}/${notificationId}`,
        {
          method: 'POST',
          
        }
      );
      fetchNotifications(page);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await authFetch(
        `${summaryApi.markAllAsRead?.url || 'http://localhost:8080/api/notifications/mark-all-read'}`,
        {
          method: 'POST',
          
        }
      );
      toast.success('All notifications marked as read');
      fetchNotifications();
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await authFetch(
        `${summaryApi.deleteNotification?.url || 'http://localhost:8080/api/notifications/delete'}/${notificationId}`,
        {
          method: 'DELETE',
          
        }
      );
      fetchNotifications(page);
      toast.success('Notification deleted');
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Delete all read notifications
  const deleteAllRead = async () => {
    const readNotifications = notifications.filter(n => n.isRead);
    for (const notif of readNotifications) {
      await deleteNotification(notif._id);
    }
    toast.success('All read notifications deleted');
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.isRead) {
      markAsRead(notification._id);
    }

    // Navigate based on action
    if (notification.action?.link) {
      navigate(notification.action.link);
    }
  };

  useEffect(() => {
    fetchNotifications(0);
    setPage(0);
  }, [filter, typeFilter]);

  const getNotificationIcon = (type) => {
    const icons = {
      product_added: <Package size={20} />,
      product_reviewed: <Star size={20} />,
      product_liked: <Heart size={20} />,
      profile_updated: <User size={20} />,
      login: <LogIn size={20} />,
      order_status: <ShoppingBag size={20} />
    };
    return icons[type] || <Bell size={20} />;
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const date = new Date(timestamp);
    const seconds = Math.floor((now - date) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ backgroundColor: colors.bg }}>
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg transition-all hover:scale-110"
            style={{
              backgroundColor: colors.bgSecondary,
              color: colors.primary
            }}
          >
            <ArrowLeft size={24} />
          </button>
          <div className="flex-1">
            <h1 className="text-4xl font-bold" style={{ color: colors.text }}>
              Notifications
            </h1>
            <p style={{ color: colors.textSecondary }}>
              {total} total • {unreadCount} unread
            </p>
          </div>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="px-4 py-2 rounded-lg font-semibold transition-all hover:scale-105 text-white"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
              }}
            >
              Mark all as read
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Status Filter */}
          <div>
            <label style={{ color: colors.textSecondary }} className="text-sm font-semibold block mb-2">
              Status
            </label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="w-full px-4 py-3 rounded-lg font-medium outline-none"
              style={{
                backgroundColor: colors.bgSecondary,
                color: colors.text,
                border: `2px solid ${colors.border}`
              }}
            >
              <option value="all">All Notifications</option>
              <option value="unread">Unread Only</option>
              <option value="read">Read Only</option>
            </select>
          </div>

          {/* Type Filter */}
          <div>
            <label style={{ color: colors.textSecondary }} className="text-sm font-semibold block mb-2">
              Type
            </label>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-full px-4 py-3 rounded-lg font-medium outline-none"
              style={{
                backgroundColor: colors.bgSecondary,
                color: colors.text,
                border: `2px solid ${colors.border}`
              }}
            >
              {notificationTypes.map(type => (
                <option key={type.value} value={type.value}>
                  {type.icon} {type.label}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Notifications List */}
        <div className="space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div
                className="w-12 h-12 border-4 border-transparent rounded-full animate-spin"
                style={{ borderTopColor: colors.primary }}
              />
            </div>
          ) : notifications.length === 0 ? (
            <div
              className="text-center py-16 rounded-xl"
              style={{ backgroundColor: colors.bgSecondary }}
            >
              <Bell size={48} className="mx-auto mb-4" style={{ color: colors.textSecondary, opacity: 0.3 }} />
              <p style={{ color: colors.textSecondary }} className="text-lg font-medium">
                No notifications found
              </p>
              <p style={{ color: colors.textSecondary }} className="text-sm mt-2">
                You're all caught up! Check back later.
              </p>
            </div>
          ) : (
            <>
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  onClick={() => handleNotificationClick(notification)}
                  className="p-4 rounded-xl transition-all hover:shadow-lg cursor-pointer group"
                  style={{
                    backgroundColor: notification.isRead ? colors.bg : colors.primaryLight,
                    border: `1px solid ${colors.border}`,
                    opacity: notification.isRead ? 0.7 : 1
                  }}
                >
                  <div className="flex gap-4">
                    {/* Icon */}
                    <div
                      className="w-14 h-14 rounded-lg flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
                      style={{ 
                        backgroundColor: notification.color ? `${notification.color}20` : colors.primaryLight,
                        color: notification.color || colors.primary
                      }}
                    >
                      {getNotificationIcon(notification.type)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="font-bold text-base" style={{ color: colors.text }}>
                            {notification.title}
                          </h3>
                          <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
                            {notification.message}
                          </p>
                          <div className="flex items-center gap-2 mt-2 text-xs" style={{ color: colors.textSecondary }}>
                            <Clock size={14} />
                            {getTimeAgo(notification.createdAt)}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                          {!notification.isRead && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification._id);
                              }}
                              className="p-2 rounded-lg transition-all hover:scale-110"
                              style={{
                                backgroundColor: colors.primaryLight,
                                color: colors.primary
                              }}
                              title="Mark as read"
                            >
                              <CheckCheck size={18} />
                            </button>
                          )}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification._id);
                            }}
                            className="p-2 rounded-lg transition-all hover:scale-110"
                            style={{
                              backgroundColor: colors.bg,
                              color: colors.accent
                            }}
                            title="Delete notification"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </div>

                      {/* Badge for unread */}
                      {!notification.isRead && (
                        <div className="flex gap-2 items-center mt-3">
                          <span
                            className="inline-block w-2 h-2 rounded-full"
                            style={{ backgroundColor: colors.accent }}
                          />
                          <span className="text-xs font-semibold" style={{ color: colors.accent }}>
                            UNREAD
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

              {/* Delete Read Button */}
              {notifications.some(n => n.isRead) && (
                <button
                  onClick={deleteAllRead}
                  className="w-full py-2 px-4 rounded-lg text-sm font-semibold transition-all hover:scale-105 text-white"
                  style={{
                    backgroundColor: colors.accent,
                    opacity: 0.7
                  }}
                >
                  Delete all read notifications
                </button>
              )}

              {/* Pagination */}
              {hasMore && (
                <button
                  onClick={() => {
                    setPage(page + 1);
                    fetchNotifications(page + 1);
                  }}
                  className="w-full py-3 px-4 rounded-lg font-semibold transition-all hover:scale-105"
                  style={{
                    backgroundColor: colors.bgSecondary,
                    color: colors.primary,
                    border: `2px solid ${colors.border}`
                  }}
                >
                  Load more notifications
                </button>
              )}
            </>
          )}
        </div>

        {/* Info Box */}
        {notifications.length === 0 && !loading && (
          <div
            className="mt-8 p-6 rounded-xl"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <p style={{ color: colors.textSecondary }} className="text-sm">
              Notifications help you stay updated about:
            </p>
            <ul style={{ color: colors.textSecondary }} className="text-sm mt-3 space-y-2 ml-4">
              <li>• New products added to the store</li>
              <li>• Reviews on products you've reviewed</li>
              <li>• Likes on products you've liked</li>
              <li>• Your profile updates</li>
              <li>• Order status changes</li>
              <li>• Your login activity</li>
            </ul>
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        div {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default NotificationsPage;